import {
  fetchCohortsRequest,
  fetchSingleCohortRequest,
  upsertCohortRequest,
} from 'actions/cohorts';
import { CohortStats } from 'reducers/cohorts';
import { createSelector } from 'reselect';
import { Cohort, CohortAuditLog, Selector } from 'types';

export const getCohort: Selector<Cohort | null> = (state) => state.cohorts.activeCohort;

export const getCohorts: Selector<Cohort[]> = (state) => state.cohorts.cohorts;

export const getCohortsTotalCount: Selector<number> = (state) => state.cohorts.count;

export const getActiveCohortAuditLogs: Selector<CohortAuditLog[]> = (state) =>
  state.cohorts.activeCohort?.audit_logs || [];

export const getCohortsLoading: Selector<boolean> = (state) =>
  state.cohorts.loading.includes(fetchCohortsRequest.toString());

export const getSingleCohortLoading: Selector<boolean> = (state) =>
  state.cohorts.loading.includes(fetchSingleCohortRequest.toString());

export const getUpsertCohortLoading: Selector<boolean> = (state) =>
  state.cohorts.loading.includes(upsertCohortRequest.toString());

export const getCohortStats: Selector<CohortStats | null> = (state) => state.cohorts.cohortStats;

export type AuditLogBatch = {
  uuid: string;
  date: string;
  insightAssigned: number;
  preventAssigned: number;
  insightTransfers: number;
  preventTransfers: number;
  auditLogs: CohortAuditLog[];
};

export const getActiveCohortAuditLogsBatches: Selector<AuditLogBatch[]> = createSelector(
  [getCohort, getCohortStats],
  (activeCohort, cohortStats) => {
    if (!activeCohort) return [];
    const auditLogs = activeCohort.audit_logs;
    const batches: AuditLogBatch[] = [];

    auditLogs.forEach((log) => {
      const foundBatch = batches.find((b) => b.uuid === log.batch_uuid);
      const logsToAdd: CohortAuditLog[] = [];
      if (log.payload.insight) {
        logsToAdd.push({ ...log, uuid: `${log.uuid}--insight` });
      }
      if (log.payload.prevent) {
        logsToAdd.push({ ...log, uuid: `${log.uuid}--prevent` });
      }
      if (!log.payload.prevent && !log.payload.insight) {
        logsToAdd.push({ ...log, uuid: `${log.uuid}` });
      }

      if (foundBatch) {
        foundBatch.auditLogs = [...foundBatch.auditLogs, ...logsToAdd];
      } else {
        batches.push({
          uuid: log.batch_uuid,
          date: log.created_at || '',
          auditLogs: logsToAdd,
          insightAssigned: 0,
          preventAssigned: 0,
          insightTransfers: 0,
          preventTransfers: 0,
        });
      }
    });

    batches.forEach((batch) => {
      const batchStats = cohortStats?.batches.find((b) => b.batch_uuid === batch.uuid);
      if (batchStats) {
        batch.insightTransfers = batchStats.license_transfers.insight;
        batch.preventTransfers = batchStats.license_transfers.prevent;
      }

      batch.auditLogs.forEach((log) => {
        if (log.payload.insight) {
          batch.insightAssigned += 1;
        }
        if (log.payload.prevent) {
          batch.preventAssigned += 1;
        }
      });
      batch.auditLogs = batch.auditLogs.sort((a, b) => {
        if (a.type === 'membership_remove' && b.type === 'membership_add') {
          return -1;
        }
        if (a.type === 'membership_add' && b.type === 'membership_remove') {
          return 1;
        }
        return 0;
      });
      batch.auditLogs = batch.auditLogs.sort((a, b) => {
        if (a.uuid.includes('insight') && b.uuid.includes('prevent')) {
          return -1;
        }
        if (a.uuid.includes('prevent') && b.uuid.includes('insight')) {
          return 1;
        }
        return 0;
      });
    });

    return batches.sort((a, b) => (a.date > b.date ? -1 : 1));
  }
);
